import { ApiError } from './api.js';

/**
 * An in-browser stand-in for POST /epc/ui/origin, for `npm run dev` review without a BFF. Loaded
 * only with ?stubHost=1&stubOrigin=1 (see app.js): the Playwright smoke test keeps intercepting
 * the real route, and the deployed iframe can never reach this.
 *
 * The loan is the handoff's obviously-sample one. The mock's Prototype Controls become query
 * flags, consumed once so the flows that re-read the origin (Update credentials, Reconnect) move
 * on the way they would against Encompass:
 *   ?creds=0             -> needsCredentials on the first read (screen 9)
 *   ?existing=processing -> an order in progress (screen 7, processing variant)
 *   ?existing=completed  -> a delivered order (screen 7, completed variant)
 *   ?onboarded=0         -> 403 TENANT_NOT_ONBOARDED (screen 11a)
 *   ?expired=1           -> 401 PAT_REJECTED on the first read (screen 11b, then reconnects)
 *   ?origin=fail         -> 502 ORIGIN_INCOMPLETE (screen 11c)
 *   ?missing=cityzip     -> loanReadiness on the first read (gap 1: no city/ZIP, Start order held;
 *                           Check again then reads the fixed loan)
 *   ?missing=docprep     -> several DocPrep gaps plus recommendations, on the first read
 */
export const STUB_LOAN = Object.freeze({
  loanId: 'stub-loan-1',
  loanNumber: 'PBG-2026-004417',
  borrowers: ['Marcus A. Delgado', 'Renata J. Delgado'],
  property: '4827 San Jacinto Trace, Round Rock, TX 78665',
  closingDate: '2026-06-24',
  loanAmount: 415000,
});

export const STUB_EXISTING_TRANSACTION = 'txn_existing';
export const STUB_EXISTING_REFERENCE = 'PBG-CP-2026-481204';

export const STUB_EXISTING_DOCUMENTS = Object.freeze([
  { id: 'cd', name: 'Closing Disclosure Package', type: 'Closing Disclosure' },
  { id: 'note', name: 'Promissory Note', type: 'Note' },
  { id: 'dot', name: 'Deed of Trust', type: 'Security Instrument' },
  { id: 'afl', name: 'Attorney Findings Letter', type: 'Attorney Docs' },
]);

export function createStubOriginLoader(params, { now = () => Date.now(), delayMs = 520 } = {}) {
  let firstRead = true;
  return async () => {
    if (delayMs) await new Promise((r) => setTimeout(r, delayMs));
    const first = firstRead;
    firstRead = false;

    if (params.get('onboarded') === '0') {
      throw new ApiError({ status: 403, errorCode: 'TENANT_NOT_ONBOARDED', correlationId: 'stub-corr-403' });
    }
    if (params.get('origin') === 'fail') {
      throw new ApiError({ status: 502, errorCode: 'ORIGIN_INCOMPLETE', correlationId: 'stub-corr-502' });
    }
    if (params.get('expired') === '1' && first) {
      throw new ApiError({ status: 401, errorCode: 'PAT_REJECTED', correlationId: 'stub-corr-401' });
    }

    const existing = params.get('existing');
    const completed = existing === 'completed';
    // &loan=0: the degraded context the BFF serves while EPC-1078's data entitlements are
    // unapplied -- every field empty, loanNumber "" (2026-09-30). The card renders dashes.
    const degraded = params.get('loan') === '0';
    const missing = params.get('missing') === 'cityzip' && first;
    const docprep = params.get('missing') === 'docprep' && first;
    return {
      tenant: { displayName: 'Sandbox Lender' },
      loanReadiness: missing ? {
        missing: ['city', 'ZIP code'], fields: ['property'], blocking: true,
        message: "This loan is missing the subject property's city and ZIP code in Encompass. Add them, then order again.",
        items: ['Subject property: city and ZIP code'], recommended: [],
      } : docprep ? {
        missing: ['loan type', 'legal description', 'SSN', 'date of birth', 'settlement agent'],
        fields: ['loanType', 'legalDescription', 'borrowers', 'settlementAgent'], blocking: true,
        message: "This loan is missing the loan type, the legal description, Marcus A. Delgado's SSN and date of birth, "
          + 'and the settlement agent in Encompass. Add them, then order again.',
        items: ['Loan type', 'Legal description', 'Marcus A. Delgado: SSN and date of birth', 'Settlement agent'],
        recommended: ['PUD / subdivision name', 'Renata J. Delgado: current address', 'Seller: phone and email'],
      } : null,
      loan: degraded
        ? { loanId: '', loanNumber: '', borrowers: [], property: null, closingDate: null, loanAmount: null }
        : { ...STUB_LOAN, ...(missing ? { property: '4827 San Jacinto Trace, TX' } : {}) },
      product: { requestType: 'CLOSING_DOC_DELIVERY', optionsSchema: null },
      needsCredentials: params.get('creds') === '0' && first,
      existingOrder: existing ? {
        transactionId: STUB_EXISTING_TRANSACTION,
        status: completed ? 'Completed' : 'Processing',
        statusDetail: completed ? null : 'Generating documents',
        orderReference: STUB_EXISTING_REFERENCE,
        updatedUtc: new Date(now() - 20 * 60 * 1000).toISOString(),
        documents: completed
          ? STUB_EXISTING_DOCUMENTS.map((d) => ({ id: d.id, name: d.name, target: d.type, deliveredUtc: new Date(now() - 20 * 60 * 1000).toISOString() }))
          : undefined,
      } : null,
    };
  };
}

/** The seed that lets the stub orders module answer for the stub origin's existing order. */
export function stubExistingSeed(params, { now = () => Date.now() } = {}) {
  const existing = params.get('existing');
  if (!existing) return [];
  return [{
    transactionId: STUB_EXISTING_TRANSACTION,
    referenceNumber: STUB_EXISTING_REFERENCE,
    order: { requestType: 'CLOSING_DOC_DELIVERY', documents: STUB_EXISTING_DOCUMENTS.map((d) => ({ ...d })) },
    // Completed: started long ago. Processing: started a moment ago, so Refresh sees it finish.
    startedAt: existing === 'completed' ? now() - 20 * 60 * 1000 : now(),
    // The F4 machinery banner shows on the processing screen once an in-flight order actually
    // polls as `processing` with this flag set: ?stubHost=1&stubOrigin=1&existing=completed
    // &pendingChange=1, then click Redraw (verified in-browser). It is visible for only the
    // ~4.2 s before the stub order completes. `existing=processing` alone routes to screen 7
    // (existingOrder) instead and never reaches the processing screen at all.
    pendingChangeUtc: params.get('pendingChange') === '1' ? new Date(now() - 60 * 1000).toISOString() : null,
  }];
}
